import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User as UserIcon, X, Camera, RefreshCw, Check } from 'lucide-react';
import useDialog from '../hooks/useDialog';

/**
 * Profile picture picker — camera-only. Clicking the avatar opens the in-browser camera
 * (navigator.mediaDevices.getUserMedia); file uploads are intentionally NOT supported so
 * staff capture a live photo of the person rather than uploading an arbitrary image.
 * Used by Add/Edit User and Add/Edit Patient.
 *
 * Props:
 *   file:          File | null — currently staged capture. Component shows a preview when set.
 *   existingUrl:   string | null — URL of an already-uploaded picture (when editing). Shown
 *                  in the avatar circle when no `file` is staged, so the user sees their
 *                  existing photo and only the green check-on-replace flow if they capture a
 *                  new one.
 *   onChange:      (file: File | null) => void
 *   label:         optional label rendered alongside the avatar
 *   hint:          optional helper text rendered alongside the avatar
 */
const ProfilePicturePicker = ({
  file,
  existingUrl = null,
  onChange,
  onRemoveExisting = null,
  label = 'Profile Picture',
  hint = 'Take a live photo with the camera',
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  // Object-URL preview for the staged capture. Revoked on unmount / next file so we
  // don't leak blob URLs.
  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The avatar shows, in priority order: staged capture preview → existing uploaded
  // picture → blank placeholder icon.
  const showImage = previewUrl || existingUrl;

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCameraOpen(true); }}
          className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
          title={showImage ? 'Retake photo' : 'Take photo'}
        >
          {showImage ? (
            <img src={showImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <UserIcon className="h-8 w-8 text-gray-400" />
          )}
        </button>
        {/* Remove (X) badge. When a new capture is staged it clears it (reverting to the
            existing photo / placeholder). When only an existing photo is shown (Edit mode),
            it removes that photo — onRemoveExisting flags it for deletion on save. */}
        {(file || (existingUrl && onRemoveExisting)) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (file) {
                onChange(null);
              } else {
                onRemoveExisting();
              }
            }}
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-500"
            title={file ? 'Remove captured photo' : 'Remove picture'}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-700">{label}</div>
        <div className="text-xs text-gray-500">{hint}</div>
      </div>

      {/* Camera modal — renders only when cameraOpen so the stream isn't held
          unnecessarily. Lives as a portal-like overlay above the form modal. */}
      {cameraOpen && (
        <CameraCaptureModal
          onCapture={(capturedFile) => { onChange(capturedFile); setCameraOpen(false); }}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </div>
  );
};

// =========================================================================
// CameraCaptureModal — full-screen overlay. Asks for camera permission, shows
// a live preview, captures a still frame to canvas, returns a File via onCapture.
// Stops the MediaStream tracks on close to release the camera light.
// =========================================================================
const CameraCaptureModal = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  // After capture we show the still + Retake/Use buttons. snapshotUrl is the
  // object-URL preview; snapshotFile is what we pass back to the caller.
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [snapshotFile, setSnapshotFile] = useState(null);
  const cameraDialogRef = useDialog(onClose, { confirmDiscard: false });

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Acquire camera on mount. If the browser denies or there's no camera, surface a
  // friendly error message instead of a console-only DOMException.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Your browser does not support camera capture. Try a different browser or device.');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 640 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (e) {
        if (e?.name === 'NotAllowedError') {
          setError('Camera access was blocked. Allow camera permission in the browser and try again.');
        } else if (e?.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError('Could not start camera: ' + (e?.message || 'unknown error'));
        }
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [stopStream]);

  // Revoke the snapshot preview URL when it changes / on unmount.
  useEffect(() => {
    if (!snapshotUrl) return;
    return () => URL.revokeObjectURL(snapshotUrl);
  }, [snapshotUrl]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    // Use the actual video dimensions so the snapshot isn't stretched.
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 640;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const filename = `profile_camera_${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      setSnapshotFile(file);
      setSnapshotUrl(URL.createObjectURL(blob));
    }, 'image/jpeg', 0.9);
  };

  const handleRetake = () => {
    if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
    setSnapshotUrl(null);
    setSnapshotFile(null);
  };

  const handleUse = () => {
    if (snapshotFile) onCapture(snapshotFile);
  };

  return (
    <div ref={cameraDialogRef} className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Camera className="h-5 w-5" />
            <h3 className="font-semibold">Take a profile photo</h3>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          {error ? (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
              {error}
            </div>
          ) : (
            <div className="bg-black rounded-xl overflow-hidden aspect-square flex items-center justify-center">
              {snapshotUrl ? (
                <img src={snapshotUrl} alt="Snapshot" className="w-full h-full object-cover" />
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          )}
          {/* Hidden canvas — used to extract a still frame from the video stream. */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors"
          >
            Cancel
          </button>
          {!error && (snapshotUrl ? (
            <>
              <button
                type="button"
                onClick={handleRetake}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl font-medium transition-colors inline-flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retake
              </button>
              <button
                type="button"
                onClick={handleUse}
                className="px-5 py-2 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                Use Photo
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleCapture}
              className="px-5 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <Camera className="h-4 w-4" />
              Capture
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfilePicturePicker;
